import { useState, useEffect, FormEvent } from "react";
import { Link, useNavigate } from "react-router";
import { useCustomerAuth } from "../../context/CustomerAuthContext";
import { usePageTitle } from "../../hooks/usePageTitle";

interface ProfileData {
  firstName: string;
  lastName: string;
  phone: string;
  default_shipping_address: string;
  default_shipping_city: string;
  default_shipping_state: string;
  default_shipping_zip: string;
}

export default function Profile() {
  usePageTitle("My Profile");
  const { customer, checkAuth } = useCustomerAuth();
  const navigate = useNavigate();

  const [profileData, setProfileData] = useState<ProfileData>({
    firstName: "",
    lastName: "",
    phone: "",
    default_shipping_address: "",
    default_shipping_city: "",
    default_shipping_state: "",
    default_shipping_zip: "",
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await fetch("/api/customer/profile", {
        credentials: "include",
      });

      if (response.ok) {
        const data = await response.json();
        setProfileData({
          firstName: data.first_name || "",
          lastName: data.last_name || "",
          phone: data.phone || "",
          default_shipping_address: data.default_shipping_address || "",
          default_shipping_city: data.default_shipping_city || "",
          default_shipping_state: data.default_shipping_state || "",
          default_shipping_zip: data.default_shipping_zip || "",
        });
      }
    } catch (error) {
      console.error("Error fetching profile:", error);
    } finally {
      setLoading(false);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!profileData.firstName.trim()) {
      newErrors.firstName = "First name is required";
    }

    if (!profileData.lastName.trim()) {
      newErrors.lastName = "Last name is required";
    }

    if (profileData.phone && !/^\d{10}$/.test(profileData.phone.replace(/\D/g, ""))) {
      newErrors.phone = "Phone must be 10 digits";
    }

    if (profileData.default_shipping_zip && !/^\d{5}$/.test(profileData.default_shipping_zip)) {
      newErrors.default_shipping_zip = "ZIP code must be 5 digits";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSuccess(false);

    if (!validateForm()) {
      return;
    }

    setSaving(true);
    setErrors({});

    try {
      const response = await fetch("/api/customer/profile", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(profileData),
      });

      if (response.ok) {
        setSuccess(true);
        // Refresh customer data in context
        await checkAuth();

        // Scroll to top to show success message
        window.scrollTo({ top: 0, behavior: "smooth" });

        // Auto-hide success message after 3 seconds
        setTimeout(() => setSuccess(false), 3000);
      } else {
        const errorData = await response.json();
        setErrors({ general: errorData.error || "Failed to update profile" });
      }
    } catch (error) {
      setErrors({ general: "An unexpected error occurred" });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-12">
        <div className="text-center">Loading...</div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-12">
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-3xl font-bold">Edit Profile</h1>
        <Link to="/account" className="btn-secondary">
          Back to Dashboard
        </Link>
      </div>

      {success && (
        <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded mb-6">
          Profile updated successfully!
        </div>
      )}

      {errors.general && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded mb-6">
          {errors.general}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Profile Picture Section */}
        <div className="card">
          <h2 className="text-xl font-bold mb-4">Profile Picture</h2>
          <div className="flex items-center gap-4">
            {customer?.avatarUrl ? (
              <img
                src={customer.avatarUrl}
                alt={`${customer.firstName} ${customer.lastName}`}
                className="w-20 h-20 rounded-full"
              />
            ) : (
              <div className="w-20 h-20 rounded-full bg-gray-200 flex items-center justify-center">
                <span className="text-2xl font-bold text-gray-600">
                  {customer?.firstName.charAt(0)}{customer?.lastName.charAt(0)}
                </span>
              </div>
            )}
            <div>
              {customer?.oauthProvider ? (
                <p className="text-sm text-gray-600">
                  Your profile picture is managed by {customer.oauthProvider === "google" ? "Google" : "Facebook"}.
                </p>
              ) : (
                <p className="text-sm text-gray-600">
                  Custom avatar upload coming soon!
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Personal Information */}
        <div className="card">
          <h2 className="text-xl font-bold mb-4">Personal Information</h2>
          <div className="space-y-4">
            {/* Email (Read-only) */}
            <div>
              <label className="block text-sm font-medium mb-1">Email Address</label>
              <input
                type="email"
                value={customer?.email || ""}
                disabled
                className="input w-full bg-gray-100 cursor-not-allowed"
              />
              <p className="text-xs text-gray-500 mt-1">Email cannot be changed</p>
            </div>

            {/* First Name */}
            <div>
              <label htmlFor="firstName" className="block text-sm font-medium mb-1">
                First Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="firstName"
                value={profileData.firstName}
                onChange={(e) => setProfileData({ ...profileData, firstName: e.target.value })}
                className={`input w-full ${errors.firstName ? "border-red-500" : ""}`}
                disabled={saving}
                required
              />
              {errors.firstName && (
                <p className="text-red-500 text-sm mt-1">{errors.firstName}</p>
              )}
            </div>

            {/* Last Name */}
            <div>
              <label htmlFor="lastName" className="block text-sm font-medium mb-1">
                Last Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="lastName"
                value={profileData.lastName}
                onChange={(e) => setProfileData({ ...profileData, lastName: e.target.value })}
                className={`input w-full ${errors.lastName ? "border-red-500" : ""}`}
                disabled={saving}
                required
              />
              {errors.lastName && (
                <p className="text-red-500 text-sm mt-1">{errors.lastName}</p>
              )}
            </div>

            {/* Phone */}
            <div>
              <label htmlFor="phone" className="block text-sm font-medium mb-1">
                Phone Number
              </label>
              <input
                type="tel"
                id="phone"
                value={profileData.phone}
                onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                className={`input w-full ${errors.phone ? "border-red-500" : ""}`}
                placeholder="(555) 123-4567"
                disabled={saving}
              />
              {errors.phone && (
                <p className="text-red-500 text-sm mt-1">{errors.phone}</p>
              )}
            </div>
          </div>
        </div>

        {/* Default Shipping Address */}
        <div className="card">
          <h2 className="text-xl font-bold mb-4">Default Shipping Address</h2>
          <div className="space-y-4">
            {/* Street Address */}
            <div>
              <label htmlFor="address" className="block text-sm font-medium mb-1">
                Street Address
              </label>
              <input
                type="text"
                id="address"
                value={profileData.default_shipping_address}
                onChange={(e) => setProfileData({ ...profileData, default_shipping_address: e.target.value })}
                className="input w-full"
                placeholder="123 Main St"
                disabled={saving}
              />
            </div>

            {/* City */}
            <div>
              <label htmlFor="city" className="block text-sm font-medium mb-1">
                City
              </label>
              <input
                type="text"
                id="city"
                value={profileData.default_shipping_city}
                onChange={(e) => setProfileData({ ...profileData, default_shipping_city: e.target.value })}
                className="input w-full"
                placeholder="New York"
                disabled={saving}
              />
            </div>

            {/* State and ZIP */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="state" className="block text-sm font-medium mb-1">
                  State
                </label>
                <input
                  type="text"
                  id="state"
                  value={profileData.default_shipping_state}
                  onChange={(e) => setProfileData({ ...profileData, default_shipping_state: e.target.value })}
                  className="input w-full"
                  placeholder="NY"
                  maxLength={2}
                  disabled={saving}
                />
              </div>
              <div>
                <label htmlFor="zip" className="block text-sm font-medium mb-1">
                  ZIP Code
                </label>
                <input
                  type="text"
                  id="zip"
                  value={profileData.default_shipping_zip}
                  onChange={(e) => setProfileData({ ...profileData, default_shipping_zip: e.target.value })}
                  className={`input w-full ${errors.default_shipping_zip ? "border-red-500" : ""}`}
                  placeholder="10001"
                  maxLength={5}
                  disabled={saving}
                />
                {errors.default_shipping_zip && (
                  <p className="text-red-500 text-sm mt-1">{errors.default_shipping_zip}</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Password Section */}
        <div className="card">
          <h2 className="text-xl font-bold mb-4">Password</h2>
          {customer?.oauthProvider ? (
            <p className="text-sm text-gray-600">
              You're signed in with {customer.oauthProvider === "google" ? "Google" : "Facebook"}.
              Password is not required for OAuth accounts.
            </p>
          ) : (
            <div>
              <p className="text-sm text-gray-600 mb-4">
                To change your password, please contact support or use the password reset feature (coming soon).
              </p>
              <button
                type="button"
                className="btn-secondary"
                onClick={() => alert("Password change functionality coming soon!")}
              >
                Change Password
              </button>
            </div>
          )}
        </div>

        {/* Save Button */}
        <div className="flex gap-4">
          <button
            type="submit"
            disabled={saving}
            className="btn-primary flex-1"
          >
            {saving ? "Saving..." : "Save Changes"}
          </button>
          <Link to="/account" className="btn-secondary">
            Cancel
          </Link>
        </div>
      </form>
    </div>
  );
}
